<script setup lang="ts">
  import { Button } from '@/components/ui/button'
  import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
  import { Dialog, DialogTrigger, DialogContent } from '@/components/ui/dialog'
  import { inject, ref, computed, type Ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
  import {
    Paperclip,
    Mic,
    SendHorizontal,
    Loader2,
    Bookmark,
    FilePlus2,
    CheckCircle,
    Info,
    Upload,
    Sparkles
  } from 'lucide-vue-next'
  import type { AIChatSession, CustomerCase } from '@/types/generated'
  import { AiChatSessionsService } from '@/types/generated'
  import { useTranslation } from '@/composables/useTranslation'
  import { ROUTES } from '@/router/routes'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { emitter } from '@/store/app/case/detail/eventEmitter'
  import PromptTemplatesDialog from './PromptTemplatesDialog.vue'

  const { t } = useTranslation({
    en: {
      YourMessage: 'Your message...',
      Generating: 'Generating...',
      SendMessage: 'Send message',
      AttachFile: 'Attach file',
      RecordVoice: 'Record voice message',
      PromptTemplates: 'Prompt templates',
      GenerateDocument: 'Generate document',
      AllDocumentsAnalyzed: 'All documents analyzed',
      DocumentsInAnalysis: '{count} Documents still in analysis',
      DocumentsBeingAnalyzed: 'Documents are being analyzed - AI may produce incorrect results',
      Processing: 'Processing:',
      DocumentsReady: 'Documents ready for AI',
      Uploading: 'Uploading:',
      DataExtraction: 'Data Extraction',
      BasicAIAnalysis: 'Basic AI Analysis',
      Summary: 'Summary',
      AdvancedAIAnalysis: 'Advanced AI Analysis',
      SummaryEmbedding: 'Summary Embedding',
      Analysis: 'Analysis',
      Queued: 'Queued',
      EnhancePrompt: 'Enhance Prompt',
      Enhancing: 'Enhancing'
    },
    de: {
      YourMessage: 'Ihre Nachricht...',
      Generating: 'Generiere...',
      SendMessage: 'Nachricht senden',
      AttachFile: 'Datei anhängen',
      RecordVoice: 'Sprachnachricht aufnehmen',
      PromptTemplates: 'Promptvorlagen',
      GenerateDocument: 'Dokument generieren',
      AllDocumentsAnalyzed: 'Alle Dokumente analysiert',
      DocumentsInAnalysis: '{count} Dokumente noch in Analyse',
      DocumentsBeingAnalyzed:
        'Dokumente werden analysiert – die KI kann inkorrekte Ergebnisse produzieren',
      Processing: 'Verarbeitung:',
      DocumentsReady: 'Dokumente sind KI-bereit',
      Uploading: 'Wird hochgeladen:',
      DataExtraction: 'Datenextraktion',
      BasicAIAnalysis: 'Grundlegende KI-Analyse',
      Summary: 'Zusammenfassung',
      AdvancedAIAnalysis: 'Fortgeschrittene KI-Analyse',
      SummaryEmbedding: 'Zusammenfassungs-Embedding',
      Analysis: 'Analyse',
      Queued: 'In Warteschlange',
      EnhancePrompt: 'Prompt verbessern',
      Enhancing: 'Verbessert'
    }
  })

  // Inject case data and chat functionality
  const caseData = inject<Ref<CustomerCase | null>>('caseData', ref(null))
  const sendChatMessage = inject(
    'sendChatMessage',
    async (session: AIChatSession | null, message: string) => {}
  )
  const isSessionGenerating = inject<(uuid: string) => boolean>('isSessionGenerating', () => false)
  const activeChatSession = inject<Ref<AIChatSession | null>>('activeChatSession', ref(null))
  const isActiveSessionGenerating = computed(() => {
    return activeChatSession.value && isSessionGenerating(activeChatSession.value.uuid)
  })
  const userChatMessage = ref('')
  const isStoreLoading = ref(true)
  const textareaRef = ref<HTMLTextAreaElement | null>(null)
  // Use the case store for document tracking
  const caseStore = useCaseStore()

  // --- Ultra-Simple Document Processing Status ---
  // Direct computed properties for UI state
  const isProcessing = computed(() => {
    const result = caseStore.processingDocumentsCount > 0
    console.log('isProcessing computed:', result, 'count:', caseStore.processingDocumentsCount)
    return result
  })
  const hasCriticalErrors = computed(() => caseStore.documentsWithCriticalErrors.length > 0)
  const hasDocuments = computed(() => (caseStore.caseRecord?.documents?.length || 0) > 0)

  // Computed property for total documents processing (including uploads)
  const totalProcessingCount = computed(() => caseStore.processingDocumentsCount)

  // Success message handling
  const showSuccessMessage = ref(false)
  let successMessageTimeoutId: number | null = null

  // Add a flag to track previous processing state
  const wasPreviouslyProcessing = ref(false)

  // Initialize the previous state when component mounts
  onMounted(() => {
    // Set initial state after a brief delay to ensure store is loaded
    setTimeout(() => {
      wasPreviouslyProcessing.value = isProcessing.value
      console.log('Initial processing state:', isProcessing.value)
    }, 500)
  })

  // Watch for processing state changes
  watch(
    () => isProcessing.value,
    (isProcessingNow) => {
      console.log(
        'Processing state changed:',
        isProcessingNow,
        'previous:',
        wasPreviouslyProcessing.value
      )

      // If we just finished processing (was true, now false)
      if (
        wasPreviouslyProcessing.value &&
        !isProcessingNow &&
        hasDocuments.value &&
        !hasCriticalErrors.value
      ) {
        console.log('Documents finished processing successfully, showing success message')
        console.log(
          'hasDocuments:',
          hasDocuments.value,
          'hasCriticalErrors:',
          hasCriticalErrors.value
        )

        // Show success message
        showSuccessMessage.value = true

        // Auto-hide after 2 seconds
        if (successMessageTimeoutId) clearTimeout(successMessageTimeoutId)
        successMessageTimeoutId = window.setTimeout(() => {
          showSuccessMessage.value = false
          successMessageTimeoutId = null
        }, 2000)
      }

      // Update the previous state for next comparison
      wasPreviouslyProcessing.value = isProcessingNow
    }
  )

  // Reset success flag when new documents are added or processing starts
  watch(
    () => [caseStore.caseRecord?.documents?.length, caseStore.activeUploadsArray.length],
    () => {
      // If new documents are added or uploads start, hide success message
      showSuccessMessage.value = false
    }
  )

  // Also hide success message if processing starts again
  watch(
    () => isProcessing.value,
    (isProcessingNow) => {
      if (isProcessingNow) {
        showSuccessMessage.value = false
      }
    }
  )

  // Clean up on unmount
  onBeforeUnmount(() => {
    if (successMessageTimeoutId) clearTimeout(successMessageTimeoutId)
  })

  // ---------------
  // Chat functionality
  // ---------------

  const sendMessage = () => {
    if (userChatMessage.value.trim() && !isActiveSessionGenerating.value) {
      sendChatMessage(activeChatSession.value, userChatMessage.value.trim())
      userChatMessage.value = ''
      // Reset textarea height after sending
      nextTick(() => {
        adjustTextareaHeight()
      })
    }
  }

  // ---------------
  // Prompt Enhancement functionality
  // ---------------

  const isEnhancingPrompt = ref(false)
  const enhancementError = ref<string | null>(null)
  const showEnhanceTooltip = ref(false)

  const enhancePrompt = async () => {
    if (
      !userChatMessage.value.trim() ||
      isEnhancingPrompt.value ||
      isActiveSessionGenerating.value
    ) {
      return
    }

    isEnhancingPrompt.value = true
    enhancementError.value = null

    try {
      const response = await AiChatSessionsService.aiChatSessionsEnhancePromptCreate({
        requestBody: {
          content: userChatMessage.value.trim()
        }
      })

      // Replace the user's input with the enhanced prompt
      userChatMessage.value = response.enhanced_content
      // Adjust textarea height for the new content
      nextTick(() => {
        adjustTextareaHeight()
      })
    } catch (error) {
      console.error('Failed to enhance prompt:', error)
      enhancementError.value = 'Failed to enhance prompt. Please try again.'

      // Auto-hide error message after 5 seconds
      setTimeout(() => {
        enhancementError.value = null
      }, 5000)
    } finally {
      isEnhancingPrompt.value = false
    }
  }

  const isPromptPopoverOpen = inject<Ref<boolean>>('isPromptPopoverOpen', ref(false))

  const closePromptPopover = () => {
    isPromptPopoverOpen.value = false
  }

  // Handle prefill from template
  const prefillFromTemplate = (content: string) => {
    userChatMessage.value = content
    // Adjust textarea height for the prefilled content
    nextTick(() => {
      adjustTextareaHeight()
    })
  }

  const handleKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  // Auto-resize textarea functionality
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.value
    if (!textarea) return

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto'

    // Calculate line height (approximately 1.25rem with leading-5)
    const lineHeight = 20 // 1.25rem = 20px
    const minRows = 3
    const maxRows = 9
    const minHeight = lineHeight * minRows
    const maxHeight = lineHeight * maxRows

    // Get the scroll height and constrain it
    const scrollHeight = textarea.scrollHeight
    const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight)

    // Set the new height
    textarea.style.height = `${newHeight}px`

    // Enable/disable scrolling based on whether we've reached max height
    if (scrollHeight > maxHeight) {
      textarea.style.overflowY = 'auto'
    } else {
      textarea.style.overflowY = 'hidden'
    }
  }

  // Watch for changes in userChatMessage to adjust height
  watch(userChatMessage, () => {
    nextTick(() => {
      adjustTextareaHeight()
    })
  })

  // Initialize textarea height on mount
  onMounted(() => {
    nextTick(() => {
      adjustTextareaHeight()
    })
  })
</script>
<template>
  <!-- Message Input -->
  <div class="mt-auto border-t border-gray-200 bg-white px-4 pb-4">
    <!-- Document Analysis Status - Only one transition at the top level -->
    <transition
      name="fade"
      mode="out-in"
      :duration="{ enter: 300, leave: 500 }"
    >
      <div
        v-if="isProcessing || showSuccessMessage"
        key="status-container"
        class="mt-2 flex items-center gap-2"
      >
        <div
          :class="[
            'flex w-full items-center gap-1.5 rounded-md px-3 py-1 text-sm',
            isProcessing
              ? 'border border-purple-100 bg-purple-50 text-purple-700'
              : 'border border-green-100 bg-green-50 text-green-700'
          ]"
        >
          <!-- Status icon - no transition here -->
          <Loader2
            v-if="isProcessing"
            class="mr-1 size-4 shrink-0 animate-spin text-purple-500"
          />
          <CheckCircle
            v-else
            class="mr-1 size-4 shrink-0 text-green-500"
          />

          <span v-if="isProcessing">{{ t('DocumentsBeingAnalyzed') }}</span>
          <span v-else>{{ t('AllDocumentsAnalyzed') }}</span>

          <!-- Document processing details -->
          <div
            v-if="isProcessing"
            class="ml-auto flex items-center gap-1.5"
          >
            <div class="rounded bg-purple-100 px-2 py-0.5 text-xs text-purple-800">
              {{ totalProcessingCount }}
              {{ totalProcessingCount === 1 ? 'Dokument' : 'Dokumente' }}
            </div>
          </div>

          <div
            v-else
            class="ml-auto rounded bg-green-100 px-2 py-0.5 text-xs text-green-800"
          >
            {{ t('DocumentsReady') }}
          </div>
        </div>
      </div>
    </transition>

    <!-- Enhancement Error Message -->
    <Transition name="fade">
      <div
        v-if="enhancementError"
        class="mb-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
      >
        <div class="flex items-center gap-2">
          <Info class="size-4 shrink-0" />
          <span>{{ enhancementError }}</span>
        </div>
      </div>
    </Transition>

    <div
      class="overflow-hidden rounded-lg border border-gray-200 shadow-sm"
      :class="isProcessing || showSuccessMessage ? 'mt-2' : 'mt-4'"
    >
      <textarea
        ref="textareaRef"
        v-model="userChatMessage"
        :disabled="isActiveSessionGenerating"
        class="block w-full resize-none overflow-y-auto border-0 pb-2 pe-4 ps-2 pt-2 leading-5 focus:outline-none
          focus:ring-0 disabled:pointer-events-none disabled:opacity-50 md:text-sm
          [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300
          [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar]:w-2"
        :placeholder="t('YourMessage')"
        rows="3"
        @keydown="handleKeydown"
        @input="adjustTextareaHeight"
      ></textarea>

      <div class="mx-2 my-2 flex items-center justify-between gap-x-1">
        <!-- Button Group -->
        <div class="flex items-center gap-x-1">
          <!-- Button Group -->
          <div class="flex items-center gap-x-2">
            <!-- Prompt Templates Button with Popover -->
            <Dialog v-model:open="isPromptPopoverOpen">
              <DialogTrigger as-child>
                <button
                  type="button"
                  :disabled="isActiveSessionGenerating"
                  class="prompt-template-btn relative flex items-center justify-center gap-x-1.5 overflow-hidden rounded-md
                    px-3 py-1.5 text-sm font-medium focus:outline-none focus:ring-0 disabled:pointer-events-none
                    disabled:opacity-50"
                >
                  <!-- Sleek, modern layered effects -->
                  <div class="btn-bg absolute inset-0 z-0 rounded-md"></div>
                  <div class="btn-border z-1 absolute inset-0 rounded-md"></div>
                  <div class="subtle-glow z-1 absolute -inset-px rounded-md opacity-0"></div>
                  <div
                    class="content-wrapper relative z-10 flex items-center justify-center gap-x-1.5"
                  >
                    <Bookmark class="icon-color size-4 shrink-0" />
                    <span class="text-color font-medium">{{ t('PromptTemplates') }}</span>
                  </div>
                </button>
              </DialogTrigger>
              <DialogContent class="max-w-5xl overflow-hidden rounded-lg p-0">
                <PromptTemplatesDialog
                  @close="closePromptPopover"
                  @prefill="prefillFromTemplate"
                />
              </DialogContent>
            </Dialog>
            <!-- Document Generate Button -->
            <a
              :href="`${ROUTES.APP.CASES.CASE_DETAIL.DOCUMENT_GENERATION.replace(':uuid', caseData.uuid)}`"
              class="flex items-center justify-center gap-x-1.5 rounded-md border border-gray-300 px-2.5 py-1 text-sm
                font-medium text-gray-700 no-underline hover:bg-gray-50 focus:outline-none focus:ring-2
                focus:ring-purple-500 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
            >
              <FilePlus2 class="size-4 shrink-0" />
              {{ t('GenerateDocument') }}
            </a>
          </div>
          <!-- End Prompt Templates Button with Popover -->

          <!-- Button -->
          <button
            type="button"
            class="invisible flex size-8 items-center justify-center gap-x-1.5 rounded-full text-sm text-gray-600
              hover:bg-gray-100 focus:bg-gray-100 focus:outline-none disabled:pointer-events-none
              disabled:opacity-50"
          >
            <Paperclip class="size-4 shrink-0" />
            <span class="sr-only">{{ t('AttachFile') }}</span>
          </button>
          <!-- End Button -->
        </div>
        <!-- End Button Group -->

        <!-- Button Group -->
        <div class="flex items-center gap-x-1">
          <!-- Button -->
          <button
            type="button"
            class="invisible flex size-8 items-center justify-center gap-x-1.5 rounded-full text-sm text-gray-600
              hover:bg-gray-100 focus:bg-gray-100 focus:outline-none disabled:pointer-events-none
              disabled:opacity-50"
          >
            <Mic class="size-4 shrink-0" />
            <span class="sr-only">{{ t('RecordVoice') }}</span>
          </button>
          <!-- End Button -->

          <!-- Enhance Prompt Button -->
          <div class="relative">
            <button
              type="button"
              :disabled="isEnhancingPrompt || isActiveSessionGenerating || !userChatMessage.trim()"
              class="inline-flex size-8 shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white
                text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:bg-gray-50
                focus:outline-none disabled:pointer-events-none disabled:opacity-50"
              @click="enhancePrompt"
              @mouseenter="showEnhanceTooltip = true"
              @mouseleave="showEnhanceTooltip = false"
            >
              <span
                v-if="isEnhancingPrompt"
                class="animate-spin"
              >
                <Loader2 class="size-4" />
              </span>
              <span v-else>
                <Sparkles class="size-4 shrink-0" />
              </span>
              <span class="sr-only">
                {{ isEnhancingPrompt ? t('Enhancing') : t('EnhancePrompt') }}
              </span>
            </button>
            <!-- Custom Tooltip -->
            <Transition name="tooltip">
              <div
                v-if="showEnhanceTooltip && !isEnhancingPrompt"
                class="absolute bottom-full left-1/2 mb-2 -translate-x-1/2 transform whitespace-nowrap rounded bg-gray-900
                  px-2 py-1 text-xs text-white shadow-lg"
              >
                {{ t('EnhancePrompt') }}
                <div
                  class="absolute left-1/2 top-full -translate-x-1/2 transform border-4 border-transparent border-t-gray-900"
                ></div>
              </div>
            </Transition>
          </div>
          <!-- End Enhance Prompt Button -->

          <!-- Send Button -->
          <button
            type="button"
            :disabled="isActiveSessionGenerating || !userChatMessage.trim()"
            class="inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-purple-600 text-sm font-medium
              text-white hover:bg-purple-700 focus:bg-purple-700 focus:outline-none disabled:pointer-events-none
              disabled:opacity-50"
            @click="sendMessage"
          >
            <span
              v-if="isActiveSessionGenerating"
              class="animate-spin"
            >
              <Loader2 class="size-4" />
            </span>
            <span v-else>
              <SendHorizontal class="size-5 shrink-0" />
            </span>
            <span class="sr-only">
              {{ isActiveSessionGenerating ? t('Generating') : t('SendMessage') }}
            </span>
          </button>
          <!-- End Send Button -->
        </div>
        <!-- End Button Group -->
      </div>
    </div>
  </div>
  <!-- End Message Input -->
</template>

<style scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition:
      opacity 0.4s ease,
      transform 0.3s ease;
  }

  .fade-enter-from {
    opacity: 0;
    transform: translateY(-5px);
  }

  .fade-leave-to {
    opacity: 0;
    transform: translateY(5px);
  }

  .fade-enter-to,
  .fade-leave-from {
    opacity: 1;
    transform: translateY(0);
  }

  /* Sleek, Modern Prompt Template Button */
  .prompt-template-btn {
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 2px 4px rgba(139, 92, 246, 0.2);
  }

  .prompt-template-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(139, 92, 246, 0.3);
  }

  /* Button background with gradient */
  .btn-bg {
    background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%);
    transition: all 0.3s ease;
  }

  /* Subtle border */
  .btn-border {
    border: 1px solid rgba(139, 92, 246, 0.3);
    transition: all 0.3s ease;
  }

  /* Subtle glow effect */
  .subtle-glow {
    box-shadow: 0 0 15px rgba(139, 92, 246, 0.4);
    opacity: 1;
    transition: opacity 0.3s ease;
  }

  /* Text and icon styling */
  .icon-color {
    color: white;
    transition: color 0.3s ease;
  }

  .text-color {
    color: white;
    transition: color 0.3s ease;
  }

  /* Subtle attention-drawing pulse animation */
  .prompt-template-btn::after {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 8px;
    border: 1px solid rgba(139, 92, 246, 0.4);
    opacity: 0;
    z-index: 0;
    animation: pulse-ring 3s cubic-bezier(0.455, 0.03, 0.515, 0.955) infinite;
  }

  @keyframes pulse-ring {
    0% {
      transform: scale(0.98);
      opacity: 0.4;
    }
    50% {
      opacity: 0;
    }
    100% {
      transform: scale(1.05);
      opacity: 0;
    }
  }

  .animate-spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  /* Tooltip transitions */
  .tooltip-enter-active,
  .tooltip-leave-active {
    transition: opacity 0.15s ease;
  }

  .tooltip-enter-from,
  .tooltip-leave-to {
    opacity: 0;
  }
</style>
